import type { CSSProperties, ReactNode } from 'react'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import { useNls } from '../../nls/nlsProvider'
import './pathOnlyRow.css'

export interface PathOnlyRowProps {
  title: string
  variant?: 'timeline' | 'stripboard' | 'tile' | 'row'
  className?: string
  style?: CSSProperties
  children?: ReactNode
}

export const PathOnlyRow = ({
  title,
  variant = 'row',
  className = '',
  style,
  children,
}: PathOnlyRowProps) => {
  const { t } = useNls()
  const fallbackTitle = t('NLSPathOnlyUntitled')
  const displayTitle = title || fallbackTitle
  const accessibleName = t('NLSPathOnlyViewOnly', { title: displayTitle })

  if (variant === 'timeline') {
    return (
      <div
        role="group"
        aria-disabled="true"
        aria-label={accessibleName}
        className={`pathOnlyRow pathOnlyRow--timeline absolute overflow-hidden ${className}`}
        style={style}
      >
        <div className="pathOnlyRow__inner flex h-full items-center gap-1.5 px-2">
          <span className="pathOnlyRow__icon shrink-0">
            <LockRoundedIcon style={{ fontSize: 13 }} />
          </span>
          <span className="pathOnlyRow__title truncate">{displayTitle}</span>
        </div>
        {children}
      </div>
    )
  }

  if (variant === 'stripboard') {
    return (
      <div
        role="group"
        aria-disabled="true"
        aria-label={accessibleName}
        className={`pathOnlyRow pathOnlyRow--stripboard stripboardCard ${className}`}
        style={style}
      >
        <div className="pathOnlyRow__inner flex items-center gap-2 p-2">
          <span className="pathOnlyRow__icon shrink-0">
            <LockRoundedIcon style={{ fontSize: 14 }} />
          </span>
          <span className="pathOnlyRow__title truncate">{displayTitle}</span>
        </div>
        {children}
      </div>
    )
  }

  if (variant === 'tile') {
    return (
      <div
        role="group"
        aria-disabled="true"
        aria-label={accessibleName}
        className={`pathOnlyRow pathOnlyRow--tile explorerTile ${className}`}
        style={style}
      >
        <span className="explorerFolderIcon pathOnlyRow__icon">
          <LockRoundedIcon fontSize="inherit" />
        </span>
        <span className="explorerTileName pathOnlyRow__title">{displayTitle}</span>
        {children}
      </div>
    )
  }

  return (
    <div
      role="group"
      aria-disabled="true"
      aria-label={accessibleName}
      className={`pathOnlyRow ${className}`}
      style={style}
    >
      <span className="pathOnlyRow__icon shrink-0">
        <LockRoundedIcon style={{ fontSize: 14 }} />
      </span>
      <span className="pathOnlyRow__title truncate">{displayTitle}</span>
      {children}
    </div>
  )
}
